import type {CustomDealCategory} from '../../../types';

import {AppstoreOutlined, SortAscendingOutlined} from '@ant-design/icons';
import {BottomSheet, Button, Checkbox, Radio} from '@ds/components';
import {useEffect, useState} from 'react';

import FilterChip from '../../../components/common/FilterChip/FilterChip';
import {CUSTOM_DEAL_SORT_OPTIONS, CUSTOM_DEAL_STOCK_STATUS_OPTIONS} from '../../../constants';

type ApplyDiscountFiltersProps = {
  filters: {
    categories?: string | null;
    sort?: string | null;
    stock_status?: string | null;
    search?: string | null;
    badge?: string | null;
  };
  onChangeFilter: (name: string, value: string | null) => void;
  dealCategories?: CustomDealCategory[];
};

const ApplyDiscountFilters: React.FC<ApplyDiscountFiltersProps> = ({
  filters,
  onChangeFilter,
  dealCategories = []
}) => {
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [selectedCategoriesString, setSelectedCategoriesString] = useState(
    filters.categories || ''
  );

  useEffect(() => {
    setSelectedCategoriesString(filters.categories || '');
  }, [filters.categories]);

  const openModal = (name: string) => {
    setActiveFilter(name);
    if (name === 'categories') {
      setSelectedCategoriesString(filters.categories || '');
      return;
    }
    setSelectedValue(
      filters[name as 'sort' | 'stock_status'] != null
        ? String(filters[name as 'sort' | 'stock_status'])
        : null
    );
  };

  const closeModal = () => {
    setActiveFilter(null);
    setSelectedValue(null);
  };

  const handleCategoryToggle = (categoryId: number) => {
    const currentCategories = selectedCategoriesString
      ? selectedCategoriesString
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean)
      : [];
    const categoryIdStr = String(categoryId);
    const nextCategories = currentCategories.includes(categoryIdStr)
      ? currentCategories.filter((id) => id !== categoryIdStr)
      : [...currentCategories, categoryIdStr];
    setSelectedCategoriesString(nextCategories.join(','));
  };

  const currentOptions =
    activeFilter === 'sort' ? CUSTOM_DEAL_SORT_OPTIONS : CUSTOM_DEAL_STOCK_STATUS_OPTIONS;
  const currentTitle =
    activeFilter === 'sort'
      ? 'مرتب‌سازی'
      : activeFilter === 'stock_status'
        ? 'وضعیت موجودی'
        : 'دسته‌بندی';

  const renderButton = (name: string, label: string, icon?: React.ReactNode) => {
    const hasValue =
      name === 'categories'
        ? Boolean(filters.categories && filters.categories.trim().length > 0)
        : filters[name as 'sort' | 'stock_status'] != null;

    return (
      <FilterChip
        key={name}
        label={label}
        icon={icon}
        active={hasValue}
        onClick={() => openModal(name)}
        onClear={hasValue ? () => onChangeFilter(name, null) : undefined}
      />
    );
  };

  return (
    <>
      <div className="flex overflow-x-auto border-b border-gray-02 px-4 py-2">
        {renderButton(
          'sort',
          'مرتب‌سازی',
          <SortAscendingOutlined className="me-1 text-body-lg" aria-hidden />
        )}
        {renderButton(
          'categories',
          'دسته‌بندی',
          <AppstoreOutlined className="me-1 text-body-lg" aria-hidden />
        )}
        {renderButton('stock_status', 'موجودی')}
      </div>
      <BottomSheet
        show={Boolean(activeFilter)}
        onClose={closeModal}
        header={
          <div className="flex flex-col items-center px-4 py-2 text-subtitle-md text-neutral-fg">
            <span className="mb-4 h-1 w-8 rounded-full bg-gray-03" aria-hidden />
            <span>{currentTitle}</span>
          </div>
        }
      >
        {activeFilter === 'categories' ? (
          <div className="flex flex-col px-4 py-2">
            {dealCategories.map((category) => (
              <div key={category.id} className="py-3">
                <Checkbox
                  checked={selectedCategoriesString
                    .split(',')
                    .map((id) => id.trim())
                    .includes(String(category.id))}
                  onChange={() => handleCategoryToggle(category.id)}
                >
                  {category.title} {`(${category.product_count} کالا)`}
                </Checkbox>
              </div>
            ))}
          </div>
        ) : (
          <Radio.Group
            className="w-full"
            value={selectedValue ?? undefined}
            onChange={(event) => setSelectedValue(String(event.target.value))}
          >
            <div className="flex flex-col px-4 py-2">
              {currentOptions.map((option) => (
                <Radio
                  key={String(option.value)}
                  value={String(option.value)}
                  className="w-full border-b border-gray-22 py-4"
                >
                  {option.label}
                </Radio>
              ))}
            </div>
          </Radio.Group>
        )}
        <div className="mt-2 flex flex-col gap-2 px-4 py-3">
          <Button
            fullWidth
            size="lg"
            onClick={() => {
              onChangeFilter(
                activeFilter as string,
                activeFilter === 'categories' ? selectedCategoriesString : selectedValue
              );
              closeModal();
            }}
          >
            تایید
          </Button>
          <Button variant="ghost" size="lg" fullWidth onClick={closeModal}>
            بستن
          </Button>
        </div>
      </BottomSheet>
    </>
  );
};

export default ApplyDiscountFilters;
